import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Badge,
  Alert,
  Spinner,
  InputGroup,
  Card,
  Row,
  Col,
} from "react-bootstrap";
import {
  IconShare,
  IconCopy,
  IconTrash,
  IconToggleLeft,
  IconToggleRight,
} from "@tabler/icons-react";
import {
  generatePublicLink,
  getPublicTestInfo,
  togglePublicTest,
  deletePublicTest,
} from "../../../services/AgentsServices";
import Swal from "sweetalert2";

const PublicLinkModal = ({ show, onHide, agent }) => {
  const [loading, setLoading] = useState(false);
  const [publicLinkData, setPublicLinkData] = useState(null);
  const [hasExistingLink, setHasExistingLink] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Carregar dados ao abrir modal
  useEffect(() => {
    if (show && agent) {
      loadPublicLinkData();
    }
  }, [show, agent]);

  // Limpar dados ao fechar modal
  useEffect(() => {
    if (!show) {
      setPublicLinkData(null);
      setHasExistingLink(false);
      setError(null);
      setSuccess(null);
    }
  }, [show]);

  const loadPublicLinkData = async () => {
    if (!agent) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getPublicTestInfo(agent.id);

      if (response.data && response.data.public_url) {
        setPublicLinkData(response.data);
        setHasExistingLink(true);
      } else {
        setHasExistingLink(false);
      }
    } catch (error) {
      console.error("Erro ao carregar dados do link público:", error);
      if (error.response?.status === 404) {
        setHasExistingLink(false);
      } else {
        setError("Erro ao carregar informações do link público");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLink = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await generatePublicLink(agent.id, []);

      setPublicLinkData(response.data);
      setHasExistingLink(true);
      setSuccess("Link público criado com sucesso!");

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Erro ao criar link público:", error);
      setError(error.response?.data?.detail || "Erro ao criar link público");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      setLoading(true);
      setError(null);

      const newStatus = !publicLinkData.is_active;
      await togglePublicTest(agent.id, newStatus);

      await loadPublicLinkData();
      setSuccess(`Link ${newStatus ? 'ativado' : 'desativado'} com sucesso!`);

      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Erro ao alterar status:", error);
      setError(error.response?.data?.detail || "Erro ao alterar status do link");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLink = async () => {
    const result = await Swal.fire({
      title: "Excluir Link Público?",
      html: `
        <div style="text-align: left;">
          <p>Esta ação <strong>não pode ser desfeita</strong> e terá os seguintes efeitos:</p>
          <ul style="margin: 15px 0; padding-left: 20px;">
            <li>O link atual será <strong>invalidado permanentemente</strong></li>
            <li>Será necessário gerar um novo link (com URL diferente)</li>
            <li>Pessoas que estão usando o link atual perderão o acesso</li>
          </ul>
          <p style="color: #dc3545; font-weight: 500;">Tem certeza que deseja continuar?</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        setError(null);

        await deletePublicTest(agent.id);

        setPublicLinkData(null);
        setHasExistingLink(false);
        setSuccess("Link público excluído com sucesso!");

        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        console.error("Erro ao excluir link:", error);
        setError(error.response?.data?.detail || "Erro ao excluir link público");
      } finally {
        setLoading(false);
      }
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess("Link copiado para área de transferência!");
      setTimeout(() => setSuccess(null), 2000);
    } catch (error) {
      setError("Erro ao copiar link");
    }
  };

  const renderExistingLink = () => (
    <div>
      {/* Status do Link */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            <IconShare className="me-2" size={16} />
            Status do Link Público
          </h6>
          <Badge bg={publicLinkData.is_active ? "success" : "secondary"}>
            {publicLinkData.is_active ? "Ativo" : "Inativo"}
          </Badge>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={8}>
              <Form.Label>URL Pública:</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  value={publicLinkData.public_url}
                  readOnly
                  style={{ fontFamily: "monospace", fontSize: "13px" }}
                />
                <Button
                  variant="outline-primary"
                  onClick={() => copyToClipboard(publicLinkData.public_url)}
                >
                  <IconCopy size={16} />
                </Button>
              </InputGroup>
            </Col>
            <Col md={4}>
              <Form.Label>Ações:</Form.Label>
              <div className="d-flex gap-2">
                <Button
                  variant={publicLinkData.is_active ? "warning" : "success"}
                  size="sm"
                  onClick={handleToggleActive}
                  disabled={loading}
                  className="d-flex align-items-center"
                >
                  {publicLinkData.is_active ? (
                    <IconToggleRight className="me-1" size={16} />
                  ) : (
                    <IconToggleLeft className="me-1" size={16} />
                  )}
                  {publicLinkData.is_active ? "Desativar" : "Ativar"}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDeleteLink}
                  disabled={loading}
                >
                  <IconTrash size={16} />
                </Button>
              </div>
            </Col>
          </Row>
          <div className="mt-3">
            <Alert variant="info" className="mb-0">
              <small>A validação de acesso é feita via Turnstile (proteção contra bots). Qualquer pessoa com o link pode testar o agente.</small>
            </Alert>
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  const renderCreateForm = () => (
    <div>
      <Alert variant="info">
        <h6 className="mb-2">
          <IconShare className="me-2" size={16} />
          Criar Link Público para Teste
        </h6>
        <p className="mb-0">
          O link público permite que outras pessoas testem o agente <strong>{agent?.agent_name}</strong> sem precisar de acesso ao sistema.
          A validação de acesso é feita via Turnstile (proteção contra bots).
        </p>
      </Alert>
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <IconShare className="me-2" size={24} />
          Gestão de Link Público - {agent?.agent_name}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-3">
            {success}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-4">
            <Spinner className="mb-3" />
            <p>Carregando informações do link público...</p>
          </div>
        ) : hasExistingLink ? (
          renderExistingLink()
        ) : (
          renderCreateForm()
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
        {!hasExistingLink && (
          <Button
            variant="primary"
            onClick={handleCreateLink}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Criando...
              </>
            ) : (
              "Criar Link Público"
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default PublicLinkModal;
